import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, LoadingSpinner, Pagination } from 'components/common';
import { LuPlus, LuSearch } from 'react-icons/lu';
import NoteCard from './NoteCard';
import NoteEditor from './NoteEditor';
import { NoteEmptyState } from './ActivityEmptyStates';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * The Notes column next to the activity feed. Owns its own search/pagination
 * state and delegates loading to `onFetch`, so the project detail tab and the
 * contact/company panel can share it while reading from different slices.
 */
const NotesPanel = ({
  recipientId, notes, onFetch, perPage, pageSizeOptions, showPagination,
  createAction, updateAction, deleteAction, submittingSelector,
}) => {
  const [creating,    setCreating]    = useState(false);
  const [page,        setPage]        = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [query,       setQuery]       = useState('');

  // `onFetch` is usually an inline arrow from the parent; keeping it in a ref
  // means a new identity each render does not retrigger the load effect.
  const fetchRef = useRef(onFetch);
  fetchRef.current = onFetch;

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!recipientId) return;
    fetchRef.current({ page, perPage, q: query });
  }, [recipientId, page, perPage, query]);

  const items      = notes.data || [];
  const meta       = notes.meta;
  const hasContent = items.length > 0;
  const editorProps = { createAction, updateAction, submittingSelector };

  return (
    <div className="tw-w-full lg:tw-w-80 tw-flex-shrink-0 tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <h6 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0">
          Notes
          {meta?.total_count > 0 && (
            <span className="tw-ml-2 tw-text-xs tw-font-semibold tw-text-slate-400">{meta.total_count}</span>
          )}
        </h6>
        {(hasContent || creating || query) && (
          <Button variant="warning" size="sm" leftIcon={LuPlus}
            onClick={() => setCreating(true)} disabled={creating}>
            Note
          </Button>
        )}
      </div>

      {(hasContent || query) && !creating && (
        <div className="tw-relative">
          <LuSearch size={13} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search notes…"
            aria-label="Search notes"
            className="tw-w-full tw-pl-8 tw-pr-3 tw-h-8 tw-rounded-lg tw-border tw-border-slate-200 tw-text-xs tw-outline-none focus:tw-border-amber-400 focus:tw-ring-2 focus:tw-ring-amber-100 tw-bg-white tw-text-slate-600 tw-placeholder-slate-400"
          />
        </div>
      )}

      {creating && (
        <NoteEditor projectId={recipientId} onClose={() => setCreating(false)} {...editorProps} />
      )}

      {notes.loading && !hasContent ? (
        <div className="tw-flex tw-justify-center tw-py-6"><LoadingSpinner size="sm" /></div>
      ) : !hasContent && !creating ? (
        query
          ? <p className="tw-text-xs tw-text-slate-400 tw-text-center tw-py-6 tw-mb-0">No note matches “{query}”.</p>
          : <NoteEmptyState onCreate={() => setCreating(true)} />
      ) : (
        <div className={`tw-flex tw-flex-col tw-gap-3 ${notes.loading ? 'tw-opacity-60' : ''}`}>
          {items.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              projectId={recipientId}
              deleteAction={deleteAction}
              {...editorProps}
            />
          ))}
          {showPagination && meta && meta.total_pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={meta.total_pages}
              totalResults={meta.total_count}
              perPage={meta.per_page}
              onPageChange={setPage}
              pageSizeOptions={pageSizeOptions}
              disabled={notes.loading}
              className="tw-mt-1"
            />
          )}
        </div>
      )}
    </div>
  );
};

NotesPanel.propTypes = {
  recipientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Slice state: { data, meta, loading }. */
  notes:       PropTypes.object.isRequired,
  /** Called with { page, perPage, q } whenever the panel needs data. */
  onFetch:     PropTypes.func.isRequired,
  perPage:     PropTypes.number,
  pageSizeOptions: PropTypes.array,
  showPagination:  PropTypes.bool,
  createAction:       PropTypes.func,
  updateAction:       PropTypes.func,
  deleteAction:       PropTypes.func,
  submittingSelector: PropTypes.func,
};

NotesPanel.defaultProps = {
  recipientId:     undefined,
  perPage:         15,
  pageSizeOptions: [15, 30, 60],
  showPagination:  true,
  createAction:       undefined,
  updateAction:       undefined,
  deleteAction:       undefined,
  submittingSelector: undefined,
};

export default NotesPanel;
